import { z } from "zod";
import type { MeResponse, ReportingCapabilities, ReportingPeriod, ReportingSubscriptionInput } from "@/lib/api";
import { hasCapability } from "@/auth/capabilities";

export function reportingAdmin(me: MeResponse | null): boolean {
  return !!me?.tenant_id && hasCapability(me, "reports:manage");
}

export function subscriptionPeriod(subscription: ReportingSubscriptionInput): ReportingPeriod {
  return subscription.period ?? ({
    daily: "previous_day", weekly: "previous_week", monthly: "previous_month",
  } as const)[subscription.schedule.frequency];
}

export function reportingPeriodDescription(period: ReportingPeriod): string {
  return {
    previous_day: "previous completed local calendar day",
    previous_week: "previous 7 completed local calendar days (not an ISO week)",
    previous_month: "previous complete local calendar month",
  }[period];
}

export const timezoneSchema = z
  .string()
  .min(1)
  .refine((value) => {
    try {
      new Intl.DateTimeFormat("en", { timeZone: value }).format();
      return value === "UTC" || value.includes("/");
    } catch {
      return false;
    }
  }, "Choose a valid IANA timezone, such as Africa/Lagos or America/New_York");

export const utcInstantSchema = z
  .string()
  .datetime({ message: "Enter an absolute UTC ISO instant ending in Z" });
export const parseRecipients = (value: string): string[] =>
  value
    .split(/[,\n;]/)
    .map((v) => v.trim())
    .filter(Boolean);
export const recipientsSchema = z
  .string()
  .refine(
    (value) => parseRecipients(value).every((email) => z.string().email().safeParse(email).success),
    "Enter valid recipient email addresses",
  )
  .refine((value) => parseRecipients(value).length <= 20, "Use at most 20 recipients")
  .refine(
    (value) => {
      const recipients = parseRecipients(value).map((email) => email.toLowerCase());
      return new Set(recipients).size === recipients.length;
    },
    "Recipient email addresses must be unique (case-insensitive)",
  );

export function reportingSchema(capabilities: ReportingCapabilities, feeders: string[]) {
  return z.object({
    template: z.enum(["telemetry-readings", "executive-report"]).default("telemetry-readings"),
    source: z
      .enum(["postgres", "archive"])
      .refine((value) => capabilities.sources.includes(value), "Source unavailable"),
    feeder_ids: z
      .array(z.string())
      .min(1, "Select at least one authorized feeder")
      .max(64, "Select at most 64 feeders")
      .refine(
        (values) => values.every((id) => feeders.includes(id)),
        "Selection includes an unavailable feeder",
      ),
    columns: z
      .array(z.string())
      .refine(
        (values) => values.every((id) => capabilities.columns.some((column) => column.id === id)),
        "Selection includes an unavailable column",
      ),
    timezone: timezoneSchema,
    recipients: recipientsSchema,
    from_time: utcInstantSchema,
    to_time: utcInstantSchema,
    name: z.string().max(120, "Use at most 120 characters for the subscription name"),
    frequency: z.enum(["daily", "weekly", "monthly"]),
    period: z.enum(["previous_day", "previous_week", "previous_month"]).default("previous_day"),
    publication_delay_minutes: z.coerce.number().int().min(0).max(1440).default(0),
    month_day: z.coerce.number().int().min(1).max(31).default(1),
    time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Use HH:MM (24-hour local time)"),
    weekday: z.coerce.number().int().min(0).max(6),
    enabled: z.boolean(),
  }).superRefine((value, context) => {
    if (value.template === "telemetry-readings" && !value.columns.length) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ["columns"], message: "Select at least one column" });
    }
    if (value.template === "executive-report" &&
      (value.source !== "postgres" || value.timezone !== "Africa/Lagos")) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ["template"],
        message: "Executive workbooks require retained PostgreSQL and Africa/Lagos" });
    }
    if (value.template === "executive-report" && value.columns.length &&
      !["feeder_id", "device_timestamp_at", "device_timestamp_wat"].every((column) => value.columns.includes(column))) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ["columns"],
        message: "Supporting readings require feeder identity, UTC and WAT timestamps" });
    }
    if (value.template === "executive-report" && value.period === "previous_month") {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ["period"],
        message: "Calculated workbooks support previous day or previous 7 days; complete months can exceed retention" });
    }
  });
}

export function reportingRangeError(from: string, to: string, maxDays: number, now = Date.now()): string | null {
  if (!utcInstantSchema.safeParse(from).success || !utcInstantSchema.safeParse(to).success)
    return "Enter absolute UTC ISO instants ending in Z";
  const duration = Date.parse(to) - Date.parse(from);
  if (duration <= 0) return "End must be after start";
  if (duration > maxDays * 86400000) return `Range must not exceed ${maxDays} days`;
  if (Date.parse(to) > now + 60000) return "End must not be in the future";
  return null;
}

export function reportingTime(instant: string, timezone: string): string {
  try {
    return `${new Intl.DateTimeFormat("en-GB", { timeZone: timezone, dateStyle: "medium", timeStyle: "long" }).format(new Date(instant))} (${timezone}) · ${instant}`;
  } catch {
    return instant;
  }
}

// Keep backend-code presentation local to reporting; unknown details remain plain text.
export function reportingError(message: string): string {
  const messages: Record<string, string> = {
    test_delivery_recipient_not_allowed:
      "Test-only email delivery: the server rejected an unapproved recipient address. Use an approved test recipient or leave recipients empty for download only.",
    row_limit_exceeded: "Export row limit exceeded. Select fewer feeders or a shorter period.",
    byte_limit_exceeded: "Export byte limit exceeded. Select fewer columns, fewer feeders or a shorter period.",
    scan_limit_exceeded: "Archive scan limit exceeded. Select fewer feeders or a shorter period.",
    execution_deadline_exceeded: "The export exceeded its processing time limit. Select fewer feeders or a shorter period.",
    "queue_limit: reporting queue is full": "The reporting queue is full. Wait for existing exports to finish, then try again.",
    "subscription_limit: at most 50 subscriptions per organization": "This organization has reached its limit of 50 subscriptions. Edit an existing subscription instead.",
    archive_catalog_not_ready: "The archive catalog is not ready. Try again later or use retained PostgreSQL readings.",
    report_schema_unsupported: "The dashboard's table structure changed or is unsupported. No partial workbook was issued. Contact support before retrying.",
    report_query_failed: "The report query failed or returned incomplete results. No workbook was issued.",
    report_cell_unsupported: "The report contains a value that cannot be safely exported to Excel. No partial workbook was issued.",
    report_period_outside_retention: "Calculated workbooks require the exact period to be within the current 30-day PostgreSQL retention and not in the future. The period was not shortened.",
    report_period_precision_unsupported: "The workbook period must use millisecond precision. Its bounds were not rounded.",
    report_source_not_configured: "The calculated report source is not configured for this deployment.",
    identity_conflict: "The selected feeders share an overlapping physical meter history. Choose a non-overlapping scope; readings will not be assigned arbitrarily.",
  };
  return messages[message] ?? message;
}

export function reportingDeliveryStatus(status: string): string {
  const labels: Record<string, string> = {
    pending: "Pending email attempt",
    sending: "Sending — awaiting provider response",
    accepted: "Accepted by Gmail — not confirmation of inbox delivery",
    failed: "Email attempt failed",
    unknown: "Unknown outcome — do not blindly resend; verify the existing attempt first",
    cancelled: "Email attempt cancelled",
  };
  return labels[status] ?? `Email attempt status: ${status}`;
}
